import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
    useAddProductToCollection,
    useRemoveProductFromCollection,
} from '@/hooks/api/useCollections'
import { useProtectedAction } from '@/hooks/useProtectedAction'
import { useToast } from '@/components/shared/toast/Toast'
import type { NormalizedCollection } from '@wtf/shared'
import { ProductDto } from '@wtf/shared'

export const useCollectionToggle = (productData: ProductDto | undefined) => {
    const queryClient = useQueryClient()
    const { withAuth } = useProtectedAction()
    const { addToast } = useToast()
    const addMutation = useAddProductToCollection()
    const removeMutation = useRemoveProductFromCollection()

    const handleToggle = useCallback(
        (collectionId: string) => {
            if (!productData) return

            const currentUrlId = productData.urlId
            const collection = queryClient.getQueryData<NormalizedCollection>([
                'collection',
                collectionId,
            ])
            const collectionName = collection?.name || 'Collection'
            const productRecords = (collection?.productIds || [])
                .map((pid) =>
                    queryClient.getQueryData<ProductDto>(['product', pid])
                )
                .filter((p): p is ProductDto => !!p)
            const matchingRecord = productRecords.find(
                (p) => p.urlId === currentUrlId
            )

            if (matchingRecord) {
                removeMutation.mutate({
                    collectionId,
                    productId: matchingRecord.id,
                })
                addToast(`Removed from ${collectionName}`, 'success', 3000)
            } else {
                addMutation.mutate({
                    collectionId,
                    data: { productId: productData.id },
                })
                addToast(`Added to ${collectionName}`, 'success', 3000)
            }
        },
        [productData, queryClient, addToast, addMutation, removeMutation]
    )

    const isProductInCollection = (collectionId: string): boolean => {
        if (!productData) return false

        const collection = queryClient.getQueryData<NormalizedCollection>([
            'collection',
            collectionId,
        ])
        const productRecords = (collection?.productIds || [])
            .map((pid) =>
                queryClient.getQueryData<ProductDto>(['product', pid])
            )
            .filter((p): p is ProductDto => !!p)

        return productRecords.some(
            (p) => p.urlId === productData.urlId
        )
    }

    return {
        handleToggle: withAuth(handleToggle),
        isProductInCollection,
        isLoading: addMutation.isPending || removeMutation.isPending,
    }
}
